import { PLANS, type Plan } from './plans';

const { PREMIUM, STARTER } = PLANS;

/**
 * What each PLAN includes — the commercial axis.
 *
 * Deliberately a 1:1 shape-clone of `PERMISSIONS` in `src/Auth/rbac/permissions.ts`
 * (`key -> readonly allowed[]`, `as const satisfies`) so the pattern reads as familiar.
 * It is a *separate* map on purpose:
 *
 *   role  answers "what may this person do?"   — can be downgraded at runtime
 *                                                (`useEffectiveRole` forces READONLY
 *                                                 in presenter mode)
 *   plan  answers "what has this team bought?" — must never change mid-session
 *
 * Collapsing the two forces a cartesian product of roles (`starter_member`,
 * `starter_readonly`, …) that doubles every time a tier is added. Keep them
 * orthogonal: `useEntitlement` composes them, neither map knows about the other.
 *
 * This directory is NOT under `src/Auth/` on purpose. Plan gating is presentation and
 * revenue mechanics; the real control is the API refusing an un-entitled tenant.
 * Anything under `Auth/` eventually gets mistaken for a security boundary.
 */
export const FEATURES = {
  // ── Included in Nexoya Starter ───────────────────────────────────────────
  'performance.view': [PREMIUM, STARTER],
  'budget.view': [PREMIUM, STARTER],

  // ── Premium: standalone analysis ─────────────────────────────────────────
  /** The /kpis area — custom metrics, their charts and the compare views. */
  'metrics.view': [PREMIUM],

  // ── Premium: attribution ─────────────────────────────────────────────────
  /** The /attributions area, the funnel "see attribution insights" link and its dialog. */
  'attribution.view': [PREMIUM],
  /** Portfolio settings attribution rules, the create-portfolio activation checkbox. */
  'attribution.configure': [PREMIUM],

  // ── Premium: optimization family ─────────────────────────────────────────
  /** The Optimize portfolio tab and its launch/apply affordances. */
  'optimization.view': [PREMIUM],
  /** The Validation portfolio tab (compares predicted vs actual — downstream of optimization). */
  'validation.view': [PREMIUM],
  /** The Simulations portfolio tab. */
  'simulation.view': [PREMIUM],
  /** Risk level + budget delta blocks in portfolio general settings. */
  'optimization.settings': [PREMIUM],
  /** Per-content optimization toggles, budget limits, bulk actions, column and filter chip. */
  'optimization.contentControls': [PREMIUM],
  /** The optimizer-applied reallocation band in the budget pacing view. */
  'budget.reallocation.view': [PREMIUM],

  // ── Premium: channels ────────────────────────────────────────────────────
  /** Connecting channels outside the Nexoya Starter approved list (see `channels.ts`). */
  'channel.unrestricted': [PREMIUM],
} as const satisfies Record<string, readonly Plan[]>;

export type Feature = keyof typeof FEATURES;

/**
 * Fails CLOSED on an unrecognised key: it belongs to no plan, so it reads as locked.
 *
 * Own-property lookup rather than a bare `FEATURES[feature]`, because callers reach this
 * with values derived from user input (`featureForPortfolioTab` off `?activeTab=`). A key
 * like `toString` resolves to the inherited `Object.prototype.toString` — a *function*, so
 * a plain `?? []` fallback would not catch it — and `.includes` on a function throws a
 * TypeError that unmounts the calling page. Locking is the safe direction here: an unknown
 * key is not a paid capability, so denying it costs nothing.
 */
export function isFeatureInPlan(feature: Feature, plan: Plan): boolean {
  const plans: readonly string[] = Object.prototype.hasOwnProperty.call(FEATURES, feature)
    ? (FEATURES[feature] as readonly string[])
    : [];
  return plans.includes(plan);
}
